<script setup lang="ts">
import { useI18n } from '@/i18n';
import { canSkipPaymentForInvoice } from '@/lib/checkoutEligibility';
import { logError } from '@/lib/logError';
import rest from '@/lib/rest';
import { useBillingStore } from '@/stores/billing';
import { useUserStore } from '@/stores/user';
import { Divider, Heading, LayoutGap } from '@dabble/quillui';
import type { Stripe } from 'stripe';
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { useRouter } from 'vue-router';
import InvoiceSummary from './components/InvoiceSummary.vue';
import StripePaymentForm from './components/StripePaymentForm.vue';

const { t } = useI18n();
const billingStore = useBillingStore();
const userStore = useUserStore();
const router = useRouter();

const paymentForm = useTemplateRef<InstanceType<typeof StripePaymentForm>>('paymentForm');

const upcomingInvoice = ref<Stripe.Invoice | null>(null);
const recurringInvoice = ref<Stripe.Invoice | null>(null);
const isLoadingInvoice = ref(false);
const isActivating = ref(false);

const canSkipPayment = computed(() => canSkipPaymentForInvoice(upcomingInvoice.value));

// Stripe's deeply recursive types (Invoice → TaxId → Customer → Account → …) don't
// always unify across module boundaries; the cast lets us pass the same shape
// across a component prop without TS bailing on the recursion depth.
type StripeInvoice = Stripe.Invoice;
const upcomingInvoiceForSummary = computed<StripeInvoice | null>(
  () => upcomingInvoice.value as unknown as StripeInvoice | null,
);
const recurringInvoiceForSummary = computed<StripeInvoice | null>(
  () => recurringInvoice.value as unknown as StripeInvoice | null,
);

const planData = computed(() => billingStore.selectedPlan);
const planName = computed(() => billingStore.product?.name ?? '');
const planAmount = computed(() => planData.value?.amount ?? 0);
const planCurrency = computed(() => planData.value?.currency ?? 'usd');
const planInterval = computed(() => planData.value?.interval ?? 'month');

function getReturnUrl(): string {
  return new URL(router.currentRoute.value.fullPath, window.location.origin).href;
}

async function activatePlan(): Promise<void> {
  if (!billingStore.selectedPlan) return;
  await rest
    .post('/billing/checkout/activate')
    .header('x-uid', userStore.user?.uid || '')
    .credentials('include')
    .send({ planId: billingStore.selectedPlan.id });
  router.push('/billing/portal');
}

async function activateWithoutPayment(): Promise<void> {
  if (isActivating.value) return;
  isActivating.value = true;
  try {
    await activatePlan();
  } catch (err) {
    logError('Failed to activate plan without payment:', err);
  } finally {
    isActivating.value = false;
  }
}

async function loadUpcomingInvoice(): Promise<void> {
  if (!billingStore.selectedPlan) return;
  isLoadingInvoice.value = true;
  try {
    const result = (await rest
      .post('/billing/invoices/upcoming')
      .header('x-uid', userStore.user?.uid || '')
      .credentials('include')
      .send({ plan: billingStore.selectedPlan.id })) as {
      next: Stripe.Invoice | null;
      recurring: Stripe.Invoice | null;
    } | null;
    upcomingInvoice.value = result?.next ?? null;
    recurringInvoice.value = result?.recurring ?? null;
  } catch (err) {
    logError('Failed to load upcoming invoice:', err);
  } finally {
    isLoadingInvoice.value = false;
  }
}

onMounted(() => {
  if (!billingStore.selectedPlan) {
    router.replace('/billing/portal');
    return;
  }
  void loadUpcomingInvoice();
});

const subscribeDisabled = computed(() => {
  if (isLoadingInvoice.value) return true;
  if (canSkipPayment.value) return isActivating.value;
  const f = paymentForm.value;
  if (!f) return true;
  return f.isSubmitting || f.isInitializing || !f.isPaymentComplete;
});

const subscribeLoading = computed(() => {
  if (canSkipPayment.value) return isActivating.value;
  return paymentForm.value?.isSubmitting ?? false;
});

function onSubscribe(): void {
  if (canSkipPayment.value) {
    void activateWithoutPayment();
    return;
  }
  void paymentForm.value?.submit();
}
</script>

<template>
  <LayoutGap direction="column" size="small" fill class="checkout">
    <LayoutGap direction="column" size="xxsmall" fill>
      <Heading :level="1" no-margin>{{ t('checkout_title') }}</Heading>
      <Divider variant="secondary" size="tiny" />
    </LayoutGap>

    <LayoutGap direction="row" wrap fill size="large" class="checkout__body">
      <StripePaymentForm
        v-if="!canSkipPayment"
        ref="paymentForm"
        :get-return-url="getReturnUrl"
        :on-confirmed="activatePlan"
      />
      <InvoiceSummary
        :upcoming-invoice="upcomingInvoiceForSummary"
        :recurring-invoice="recurringInvoiceForSummary"
        :is-loading-invoice="isLoadingInvoice"
        :plan-name="planName"
        :plan-amount="planAmount"
        :plan-currency="planCurrency"
        :plan-interval="planInterval"
        :no-payment-required="canSkipPayment"
        :subscribe-disabled="subscribeDisabled"
        :subscribe-loading="subscribeLoading"
        @subscribe="onSubscribe"
      />
    </LayoutGap>
  </LayoutGap>
</template>

<style lang="scss" scoped>
.checkout {
  max-width: 60rem;

  &__body {
    align-items: flex-start;
  }
}
</style>
